import type { PaletteEntry, PatternProject, WorkMode } from "./types";

export const MAX_PATTERN_SIDE = 200;
export const MAX_UNDO = 50;

export function cellIndex(project: Pick<PatternProject, "width">, row: number, column: number): number {
  return row * project.width + column;
}

export function occupiedCount(project: Pick<PatternProject, "cells">): number {
  let total = 0;
  for (const value of project.cells) if (value !== 0) total += 1;
  return total;
}

export function completedCount(project: Pick<PatternProject, "cells" | "completed">): number {
  let total = 0;
  for (let index = 0; index < project.cells.length; index += 1) {
    if (project.cells[index] !== 0 && project.completed[index] === 1) total += 1;
  }
  return total;
}

export function countByPalette(project: Pick<PatternProject, "cells" | "completed" | "palette">) {
  const counts = new Map<number, { total: number; completed: number }>();
  for (const entry of project.palette) counts.set(entry.id, { total: 0, completed: 0 });
  for (let index = 0; index < project.cells.length; index += 1) {
    const id = project.cells[index] ?? 0;
    if (!id) continue;
    const current = counts.get(id) ?? { total: 0, completed: 0 };
    current.total += 1;
    if (project.completed[index] === 1) current.completed += 1;
    counts.set(id, current);
  }
  return counts;
}

export function selectionCount(project: Pick<PatternProject, "width" | "height" | "currentMode" | "palette">): number {
  switch (project.currentMode) {
    case "row": return project.height;
    case "column": return project.width;
    case "block5": return Math.ceil(project.width / 5) * Math.ceil(project.height / 5);
    case "color": return project.palette.length;
  }
}

export function selectionLabel(project: Pick<PatternProject, "width" | "height" | "currentMode" | "currentSelection" | "palette" | "selectedColorId">): string {
  switch (project.currentMode) {
    case "row": return `第 ${project.currentSelection + 1} 行`;
    case "column": return `第 ${project.currentSelection + 1} 列`;
    case "block5": {
      const columns = Math.ceil(project.width / 5);
      const row = Math.floor(project.currentSelection / columns) + 1;
      const column = (project.currentSelection % columns) + 1;
      return `第 ${row} 行区 · 第 ${column} 列区`;
    }
    case "color": {
      const entry = project.palette.find((item) => item.id === project.selectedColorId) ?? project.palette[project.currentSelection];
      return entry ? `${entry.code} · 颜色制作` : "选择颜色";
    }
  }
}

export function isIndexInSelection(project: PatternProject, index: number): boolean {
  const row = Math.floor(index / project.width);
  const column = index % project.width;
  switch (project.currentMode) {
    case "row": return row === project.currentSelection;
    case "column": return column === project.currentSelection;
    case "block5": {
      const blockColumns = Math.ceil(project.width / 5);
      const blockRow = Math.floor(project.currentSelection / blockColumns);
      const blockColumn = project.currentSelection % blockColumns;
      return Math.floor(row / 5) === blockRow && Math.floor(column / 5) === blockColumn;
    }
    case "color": {
      const paletteId = project.selectedColorId ?? project.palette[project.currentSelection]?.id ?? -1;
      return project.cells[index] === paletteId;
    }
  }
}

export function selectedPendingIndices(project: PatternProject): number[] {
  const indices: number[] = [];
  for (let index = 0; index < project.cells.length; index += 1) {
    if (project.cells[index] !== 0 && project.completed[index] !== 1 && isIndexInSelection(project, index)) indices.push(index);
  }
  return indices;
}

export function findNextNonEmptySelection(project: PatternProject, direction: 1 | -1): number {
  const count = selectionCount(project);
  if (!count) return 0;
  for (let offset = 1; offset <= count; offset += 1) {
    const next = (project.currentSelection + direction * offset + count) % count;
    const candidate: PatternProject = { ...project, currentSelection: next };
    if (candidate.currentMode === "color") candidate.selectedColorId = candidate.palette[next]?.id ?? null;
    if (selectedPendingIndices(candidate).length > 0) return next;
  }
  return project.currentSelection;
}

export function withMode(project: PatternProject, mode: WorkMode): PatternProject {
  const next = { ...project, currentMode: mode, currentSelection: 0 };
  if (mode === "color") next.selectedColorId = project.palette[0]?.id ?? null;
  return next;
}

export function cloneProject(project: PatternProject): PatternProject {
  return {
    ...project,
    palette: project.palette.map((entry) => ({
      ...entry,
      displayRGB: [...entry.displayRGB] as [number, number, number],
      detectedRGB: entry.detectedRGB ? [...entry.detectedRGB] as [number, number, number] : undefined,
    })),
    cells: project.cells.slice(),
    completed: project.completed.slice(),
    confidence: project.confidence.slice()
  };
}

export function recalculatePalette(project: PatternProject): PatternProject {
  const next = cloneProject(project);
  const counts = new Map<number, number>();
  for (const id of next.cells) if (id) counts.set(id, (counts.get(id) ?? 0) + 1);
  next.palette = next.palette.map((entry) => ({ ...entry, detectedCount: counts.get(entry.id) ?? 0 }));
  next.updatedAt = new Date().toISOString();
  return next;
}

const DEMO_PALETTE: PaletteEntry[] = [
  { id: 1, code: "H07", displayRGB: [30, 32, 35], detectedCount: 0, sourceCount: null, confidence: 98 },
  { id: 2, code: "C10", displayRGB: [117, 205, 232], detectedCount: 0, sourceCount: null, confidence: 99 },
  { id: 3, code: "H19", displayRGB: [244, 239, 223], detectedCount: 0, sourceCount: null, confidence: 97 },
  { id: 4, code: "G03", displayRGB: [240, 190, 151], detectedCount: 0, sourceCount: null, confidence: 98 },
  { id: 5, code: "C20", displayRGB: [69, 144, 191], detectedCount: 0, sourceCount: null, confidence: 98 },
  { id: 6, code: "M14", displayRGB: [186, 94, 92], detectedCount: 0, sourceCount: null, confidence: 96 },
  { id: 7, code: "H03", displayRGB: [78, 79, 84], detectedCount: 0, sourceCount: null, confidence: 98 },
  { id: 8, code: "H06", displayRGB: [112, 112, 116], detectedCount: 0, sourceCount: null, confidence: 98 },
  { id: 9, code: "M08", displayRGB: [137, 105, 107], detectedCount: 0, sourceCount: null, confidence: 97 },
  { id: 10, code: "E11", displayRGB: [246, 218, 198], detectedCount: 0, sourceCount: null, confidence: 97 },
  { id: 11, code: "H05", displayRGB: [148, 145, 143], detectedCount: 0, sourceCount: null, confidence: 98 },
  { id: 12, code: "F07", displayRGB: [181, 63, 91], detectedCount: 0, sourceCount: null, confidence: 96 },
  { id: 13, code: "M13", displayRGB: [125, 74, 64], detectedCount: 0, sourceCount: null, confidence: 97 },
  { id: 14, code: "F17", displayRGB: [210, 134, 90], detectedCount: 0, sourceCount: null, confidence: 97 },
  { id: 15, code: "C16", displayRGB: [53, 111, 181], detectedCount: 0, sourceCount: null, confidence: 97 },
  { id: 16, code: "C03", displayRGB: [133, 207, 231], detectedCount: 0, sourceCount: null, confidence: 98 },
  { id: 17, code: "H02", displayRGB: [249, 247, 240], detectedCount: 0, sourceCount: null, confidence: 96 },
  { id: 18, code: "F06", displayRGB: [145, 54, 45], detectedCount: 0, sourceCount: null, confidence: 96 }
];

export function createDemoProject(width = 54, height = 81): PatternProject {
  const cells = new Uint16Array(width * height);
  const centerX = width / 2;
  const centerY = height / 2;
  for (let row = 0; row < height; row += 1) {
    for (let column = 0; column < width; column += 1) {
      const dx = (column - centerX) / (width * 0.42);
      const dy = (row - centerY) / (height * 0.44);
      const index = row * width + column;
      if (dx * dx + dy * dy > 1) continue;
      if (Math.abs(dx) > 0.65 && dy < -0.28) continue;
      if (dy > 0.55 && Math.abs(dx) > 0.28) continue;
      if (Math.abs(dx) < 0.26 && dy > -0.1 && dy < 0.28) cells[index] = 4;
      else if (dy < -0.2 && dx < 0.05) cells[index] = 2;
      else if (dy > 0.38) cells[index] = 3;
      else if (dx > 0.42) cells[index] = 5;
      else cells[index] = 1;
      if (Math.abs(dx) < 0.2 && dy > 0.16 && dy < 0.34) cells[index] = 6;
    }
  }
  if (width === 54 && height === 81) {
    let occupied = occupiedCount({ cells });
    const candidates = [...cells.keys()].filter((index) => cells[index] === 0).sort((left, right) => {
      const leftX = left % width - centerX; const leftY = Math.floor(left / width) - centerY;
      const rightX = right % width - centerX; const rightY = Math.floor(right / width) - centerY;
      return leftX * leftX + leftY * leftY - (rightX * rightX + rightY * rightY);
    });
    for (const index of candidates) {
      if (occupied >= 2735) break;
      const row = Math.floor(index / width);
      cells[index] = row < height * .35 ? 2 : row > height * .7 ? 3 : 1;
      occupied += 1;
    }
  }
  const now = new Date().toISOString();
  const project: PatternProject = {
    schemaVersion: 1,
    id: crypto.randomUUID(),
    name: "2026/04/08 22:33",
    width,
    height,
    palette: DEMO_PALETTE.map((entry) => ({ ...entry })),
    cells,
    completed: new Uint8Array(cells.length),
    confidence: new Uint8Array(cells.length).fill(98),
    currentMode: "block5",
    currentSelection: Math.ceil(width / 5) * 3 + 5,
    selectedColorId: null,
    sourceImageId: "demo",
    createdAt: now,
    updatedAt: now,
    lastBackupAt: null
  };
  const completedTarget = width === 54 && height === 81 ? 1260 : 420;
  for (let index = 0, completed = 0; index < cells.length && completed < completedTarget; index += 1) {
    if (cells[index]) { project.completed[index] = 1; completed += 1; }
  }
  return recalculatePalette(project);
}
